<template>
    <div>
        <span v-if="'root' != getName" class="input-name">{{ getName }}</span>
        <span v-if="'root' != getName">(</span>
        <div v-for="(args, indexArgs) in getArgs" style="padding-left: 20px;">
            <div class="arg-navigate">
                <span class="formula-name">{{ args.getName() }} ({{ args.getType() }})</span>
                <span v-if="showRemoveButton(indexArgs, input)" @click="remove(input, indexArgs)"
                      class="remove-input">x</span>
            </div>

            <div v-if="isScalar(getInput(indexArgs, input))">
                <input type="text" @keyup="setScalarValue(getInput(indexArgs, input), $event)"
                       :value="getScalarValue(getInput(indexArgs, input))">
            </div>

            <div v-else-if="isCollection(getInput(indexArgs, input))">
                <drop class="drop-box" @drop="drop($event.data, indexArgs, input)">
                    <div v-for="(collectionInput) in getInput(indexArgs, input).getAll()">
                        <div v-if="isScalar(collectionInput)">
                            <input type="text" @keyup="setScalarValue(collectionInput, $event)"
                                   :value="getScalarValue(collectionInput)">
                        </div>

                        <recursive-drop v-else v-bind="{ input: collectionInput }"/>
                    </div>
                </drop>
            </div>

            <recursive-drop v-else-if="hasInput(indexArgs, input)" v-bind="{ input: getInput(indexArgs, input) }"/>

            <drop v-else class="drop-box" @drop="drop($event.data, indexArgs, input)"></drop>
        </div>
        <span v-if="'root' != getName">)</span>
    </div>
</template>

<script>
import {Drag, Drop, DropMask} from "vue-easy-dnd";

import Input from "@/Models/pricingRulesInterface/Input.ts";
import ScalarInput from "@/Models/pricingRulesInterface/ScalarInput.ts";
import InputInterface from "@/Models/pricingRulesInterface/InputInterface.ts";
import CollectionInput from "@/Models/pricingRulesInterface/CollectionInput.ts";
import Formula from "@/Models/pricingRulesInterface/Formula.ts";
import Argument from "@/Models/pricingRulesInterface/Argument.ts";
import FormulaService from "@/Models/pricingRulesInterface/FormulaService.ts";

export default {
    name: "recursive-drop",
    components: {
        Drag,
        Drop,
        DropMask
    },
    props: {
        input: {
            Type: InputInterface
        },
        formulaService: {
            Type: FormulaService
        }
    },
    beforeDestroy() {
        this.unmountEvents();
    },
    mounted() {
        this.$root.$on('recursive-drop:insertInputs', (formula, index, input) => {
            this.insertInputs(formula, index, input);
        });
    },
    computed: {
        getName() {
            return this.input.getName();
        },
        getArgs() {
            return this.input.getFormula().getArgs();
        }
    },
    methods: {
        unmountEvents() {
            let eventManagerService = this.$root.getEventManager();

            eventManagerService.unbindEvents([{
                name: 'insertInputs',
                componentIndex: 'recursive-drop',
            }]);
        },
        getInput(index, parentInput) {
            return parentInput.getInput(index);
        },
        hasInput(index, parentInput) {
            return parentInput.hasInput(index);
        },
        isScalar(input) {
            if (undefined === input) {
                return false;
            }

            return 'ScalarInput' === input.constructor.name;
        },
        isCollection(input) {
            if (undefined === input) {
                return false;
            }

            return 'CollectionInput' === input.constructor.name;
        },
        setScalarValue(input, event) {
            input.setValue(event.target.value);
            this.updateSerial();
        },
        getScalarValue(input) {
            return input.getValue();
        },
        dropIsScalar(formula) {
            return 0 === formula.getArgs().length;
        },
        addScalarInput(index, parentInput, value = '') {
            return parentInput.addInput(index, new ScalarInput(value));
        },
        addInput(index, formula, parentInput) {
            return parentInput.addInput(index, new Input(formula.getName(), formula.getReturnType(), formula));
        },
        addCollectionInput(index, returnType, parentInput) {
            return parentInput.addInput(index, new CollectionInput(returnType));
        },
        insertInput(formula, index, parentInput, value = '') {
            let formulaName = formula.getName();
            let argumentType = parentInput.getFormula().getArgs()[index].getType();
            let formulaReturnType = formula.getReturnType();

            if (null === argumentType) {
                argumentType = 'any';
            }

            if ('any' === formulaReturnType) {
                if (this.dropIsScalar(formula)) {
                    formulaReturnType = argumentType.replace(/\[\]$/, '');
                    if ('INPUT' !== formulaName) {
                        value = '$' + formulaName;
                    }
                } else {
                    formulaReturnType = argumentType;
                }
            }
            else if('array' === formulaReturnType && argumentType.search(/\[\]/)){
                formulaReturnType = argumentType;
            } else if ('float' === formulaReturnType) {
                formulaReturnType = 'numeric';
            }

            if ('any' === argumentType) {
                if (this.dropIsScalar(formula)) {
                    this.addScalarInput(index, parentInput, value);
                } else {
                    this.addInput(index, formula, parentInput)
                }
            } else if (argumentType === formulaReturnType) {
                if (this.dropIsScalar(formula)) {
                    this.addScalarInput(index, parentInput, value);
                } else {
                    this.addInput(index, formula, parentInput);
                }
            } else if (argumentType.replace(/\[\]$/, '') === formulaReturnType) {
                if (!this.isCollection(this.getInput(index, parentInput))) {
                    this.addCollectionInput(index, argumentType, parentInput);
                }

                let collection = parentInput.getInput(index);

                if (this.dropIsScalar(formula)) {
                    collection.addValue(new ScalarInput(value));
                } else {
                    collection.addValue(new Input(formula.getName(), formulaReturnType, formula));
                }
            } else if (null === formulaReturnType) {
                let formula = new Formula(formulaName, argumentType);
                let type = null;

                formula.getArgs().forEach((arg, index) => {
                    if (null === arg.getType()) {
                        type = argumentType
                    } else {
                        type = arg.getType();
                    }
                    formula.addArgs(new Argument(arg.getName(), type, arg.getRequired()));
                });

                this.addInput(index, formula, parentInput);
            }

            return parentInput.getInput(index);
        },
        insertInputs(mock, index, parentInput) {
            let formulaName = mock.name;
            let inputs = mock.inputs;
            let values = mock.values;
            let value = mock.value;
            let formula = this.formulaService.getByName(formulaName);

            if (undefined !== inputs) {
                if (this.isCollection(parentInput)) {
                    parentInput.addValue(new Input(formula.getName(), formula.getReturnType(), formula));
                    let input = parentInput.getValue(index);

                    inputs.forEach((item, index) => {
                        this.insertInputs(item, index, input);
                    });
                } else {
                    let input = this.addInput(index, formula, parentInput);

                    inputs.forEach((item, index) => {
                        this.insertInputs(item, index, input);
                    });
                }
            } else if (undefined !== values) {
                let argumentType = parentInput.getReturnType();
                let collection = this.addCollectionInput(index, argumentType, parentInput)

                values.forEach((item, index) => {
                    this.insertInputs(item, index, collection);
                });
            } else if (undefined !== value) {
                if (this.isCollection(parentInput)) {
                    parentInput.addValue(new ScalarInput(value));
                } else {
                    this.addScalarInput(index, parentInput, value);
                }
            }

            this.$forceUpdate();
        },
        drop(formula, index, parentInput) {
            this.insertInput(formula, index, parentInput);
            this.updateSerial();
        },
        remove(parentInput, index) {
            parentInput.removeInput(index);
            this.updateSerial();
        },
        showRemoveButton(index, parentInput) {
            let input = this.getInput(index, parentInput);
            return this.isScalar(input) || this.isCollection(input) || this.hasInput(index, parentInput)
        },
        updateSerial() {
            this.$root.$emit('dynamic-pricing-rule:getSerial');
            this.$forceUpdate();
        }
    }
};
</script>

<style>
.name {
    font-weight: bold;
    font-size: 14px;
}

.arg-navigate {
    display: inline-block;
    padding-top: 5px;
}

.formula-name {
    font-weight: bold;
    font-size: 12px;
}

.remove-input {
    cursor: pointer;
    color: red;
    padding: 5px;
    display: inline-block;
}

.row {
    padding-top: 10px;
    padding-bottom: 10px;
}
</style>


// WEBPACK FOOTER //
// DropComponent.vue?5bfa3280